import React, { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { Button, Field, Input, Select, Textarea, Tag, Modal, tomDoStatus, dataHoraBR, paraInputDataHora, diasAte } from "../components/ui";

const DIAS_SEMANA = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

/** Extrai partes da data (sem fuso: a API entrega texto — ver ui.js) */
function partesData(valor) {
    const [data, hora] = String(valor || "").split(/[ T]/);
    const [a, m, d] = data.split("-").map(Number);
    return { dia: d, mes: MESES[m - 1], ano: a, hora: (hora || "00:00").slice(0, 5), dataObj: new Date(a, m - 1, d) };
}

/** Rótulo humano de proximidade: Hoje / Amanhã / Ontem / seg 12/ago */
function rotuloProximidade(valor) {
    const dias = diasAte(valor);
    if (dias === 0) return "Hoje";
    if (dias === 1) return "Amanhã";
    if (dias === -1) return "Ontem";
    const { dataObj, dia, mes } = partesData(valor);
    return `${DIAS_SEMANA[dataObj.getDay()]} ${dia}/${mes}`;
}

const FORM_VAZIO = {
    cod_contrato: "",
    nome_compromisso: "",
    data_compromisso: "",
    local_compromisso: "",
    descricao: "",
    status_compromisso: "pendente",
    ids_responsaveis: []
};

const STATUS_COMPROMISSO = ["pendente", "comparecido", "remarcado", "perdido", "cancelado"];


const Agenda = () => {
    const { usuario } = useAuth();
    const [busca, setBusca] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
    const [apenasMeus, setApenasMeus] = useState(false);
    const [compromissos, setCompromissos] = useState([]);
    const [contratos, setContratos] = useState([]);
    const [equipe, setEquipe] = useState([]);
    const [mostrarInfo, setMostrarInfo] = useState(false);
    const [mostrarForm, setMostrarForm] = useState(false);
    const [editando, setEditando] = useState(false);
    const [selecionado, setSelecionado] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);
    const [formData, setFormData] = useState(FORM_VAZIO);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const toggleResponsavel = (id) => {
        setFormData((prev) => ({
            ...prev,
            ids_responsaveis: prev.ids_responsaveis.includes(id)
                ? prev.ids_responsaveis.filter((x) => x !== id)
                : [...prev.ids_responsaveis, id]
        }));
    };

    const carregar = useCallback(async () => {
        try {
            const { data } = await api.get(`/agenda${apenasMeus ? "?meus=1" : ""}`);
            setCompromissos(data);
        } catch (error) {
            console.error("Erro ao buscar compromissos:", error);
        }
    }, [apenasMeus]);

    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => {
        api.get("/contratos").then((r) => setContratos(r.data)).catch(() => {});
        api.get("/usuarios").then((r) => setEquipe(r.data.filter((u) => u.ativo))).catch(() => {});
    }, []);

    const abrirNovo = () => {
        setEditando(false);
        // Quem cria já entra como responsável — o caso mais comum
        setFormData({ ...FORM_VAZIO, ids_responsaveis: [usuario.id_usuario] });
        setErro(null);
        setMostrarForm(true);
    };

    const abrirEditar = () => {
        setEditando(true);
        setFormData({
            cod_contrato: selecionado.cod_contrato || "",
            nome_compromisso: selecionado.nome_compromisso || "",
            data_compromisso: paraInputDataHora(selecionado.data_compromisso),
            local_compromisso: selecionado.local_compromisso || "",
            descricao: selecionado.descricao || "",
            status_compromisso: selecionado.status_compromisso || "pendente",
            ids_responsaveis: selecionado.ids_responsaveis || []
        });
        setErro(null);
        setMostrarInfo(false);
        setMostrarForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setCarregando(true);
        setErro(null);
        try {
            if (formData.ids_responsaveis.length === 0) {
                throw new Error("Escolha pelo menos um responsável pelo compromisso.");
            }
            // Um associado só enxerga os compromissos em que é responsável: ao
            // se tirar da lista, o compromisso some da agenda dele.
            if (usuario?.papel === "associado" && !formData.ids_responsaveis.includes(usuario.id_usuario)) {
                const confirmar = window.confirm(
                    "Você não está entre os responsáveis por este compromisso. " +
                    "Ao salvar, ele deixará de aparecer na sua agenda. Continuar?"
                );
                if (!confirmar) return;
            }
            const dados = { ...formData, cod_contrato: formData.cod_contrato || null };

            if (editando) {
                await api.put(`/agenda/${selecionado.cod_compromisso}`, dados);
            } else {
                await api.post("/agenda", dados);
            }
            setMostrarForm(false);
            carregar();
            setFormData(FORM_VAZIO);
        } catch (err) {
            setErro(err.response?.data?.error || err.message);
        } finally {
            setCarregando(false);
        }
    };

    const excluir = async (cod) => {
        if (!window.confirm("Excluir este compromisso?")) return;
        try {
            await api.delete(`/agenda/${cod}`);
            setMostrarInfo(false);
            setCompromissos((prev) => prev.filter((c) => c.cod_compromisso !== cod));
        } catch (error) {
            alert(error.response?.data?.error || "Não foi possível excluir o compromisso.");
        }
    };

    const filtrados = compromissos.filter((c) => {
        const q = busca.toLowerCase();
        return (
            (c.nome_compromisso || "").toLowerCase().includes(q) ||
            (c.nome_cliente || "").toLowerCase().includes(q) ||
            (c.responsaveis || "").toLowerCase().includes(q) ||
            (c.local_compromisso || "").toLowerCase().includes(q) ||
            (c.status_compromisso || "").toLowerCase().includes(q)
        );
    });

    // Ordena por proximidade: os que ainda vão acontecer primeiro (do mais
    // próximo ao mais distante); os já passados ficam no fim, do mais recente
    // para o mais antigo.
    const ordenados = [...filtrados].sort((a, b) => {
        const da = diasAte(a.data_compromisso);
        const db = diasAte(b.data_compromisso);
        const futuroA = da >= 0, futuroB = db >= 0;
        if (futuroA !== futuroB) return futuroA ? -1 : 1;
        const cmp = String(a.data_compromisso).localeCompare(String(b.data_compromisso));
        return futuroA ? cmp : -cmp;
    });

    /** Destaque de urgência do card, só para compromissos pendentes */
    const urgencia = (c) => {
        if (c.status_compromisso !== "pendente") return "";
        const dias = diasAte(c.data_compromisso);
        if (dias < 0) return "card-agenda-vencido";
        if (dias <= 1) return "card-agenda-critico";
        if (dias <= 3) return "card-agenda-aviso";
        return "";
    };

    return (
        <div>
            <Header />

            <main className="page">
                <div className="toolbar">
                    <h2 style={{ margin: 0 }}>Agenda</h2>
                    <div className="toolbar-spacer">
                        <Button
                            variante={apenasMeus ? "primary" : "secondary"}
                            onClick={() => setApenasMeus((v) => !v)}
                        >
                            {apenasMeus ? "Minha agenda" : "Escritório"}
                        </Button>
                        <Input placeholder="Buscar" value={busca} onChange={(e) => setBusca(e.target.value)} />
                        <Button onClick={abrirNovo}>Novo compromisso</Button>
                    </div>
                </div>

                <div className="agenda-lista">
                    {ordenados.map((c) => {
                        const p = partesData(c.data_compromisso);
                        return (
                            <button
                                key={c.cod_compromisso}
                                className={`card-agenda ${urgencia(c)}`}
                                onClick={() => { setMostrarInfo(true); setSelecionado(c); }}
                            >
                                <div className="card-agenda-data">
                                    <span className="card-agenda-dia">{p.dia}</span>
                                    <span className="card-agenda-mes">{p.mes}</span>
                                    <span className="card-agenda-hora">{p.hora}</span>
                                </div>

                                <div className="card-agenda-corpo">
                                    <div className="card-agenda-topo">
                                        <span className="card-agenda-quando">{rotuloProximidade(c.data_compromisso)}</span>
                                        <Tag tom={tomDoStatus(c.status_compromisso)}>{c.status_compromisso}</Tag>
                                    </div>
                                    <h3 className="card-agenda-titulo">{c.nome_compromisso}</h3>
                                    <div className="card-agenda-meta">
                                        {c.local_compromisso && <span>📍 {c.local_compromisso}</span>}
                                        {c.nome_cliente && <span>👤 {c.nome_cliente}</span>}
                                        <span>⚖ {c.responsaveis || "sem responsável"}</span>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                    {ordenados.length === 0 && (
                        <p className="text-muted">
                            {apenasMeus ? "Você não tem compromissos." : "Nenhum compromisso encontrado."}
                        </p>
                    )}
                </div>

                {mostrarInfo && selecionado && (
                    <Modal titulo={selecionado.nome_compromisso} onFechar={() => setMostrarInfo(false)}>
                        <div className="grid-form">
                            <Field label="Data e hora"><Input value={dataHoraBR(selecionado.data_compromisso)} readOnly /></Field>
                            <Field label="Status"><Input value={selecionado.status_compromisso} readOnly /></Field>
                            <Field label="Local" className="span-2"><Input value={selecionado.local_compromisso || "—"} readOnly /></Field>
                            <Field label="Responsáveis" className="span-2"><Input value={selecionado.responsaveis || "—"} readOnly /></Field>
                            <Field label="Cliente"><Input value={selecionado.nome_cliente || "— sem contrato —"} readOnly /></Field>
                            <Field label="Criado por"><Input value={selecionado.nome_criador || "—"} readOnly /></Field>
                            <Field label="Descrição" className="span-2"><Textarea value={selecionado.descricao || ""} readOnly /></Field>
                        </div>
                        <div className="modal-acoes">
                            <Button variante="danger" onClick={() => excluir(selecionado.cod_compromisso)}>Excluir</Button>
                            <Button variante="secondary" onClick={() => setMostrarInfo(false)}>Voltar</Button>
                            <Button onClick={abrirEditar}>Editar</Button>
                        </div>
                    </Modal>
                )}

                {mostrarForm && (
                    <Modal titulo={editando ? "Editar compromisso" : "Novo compromisso"} onFechar={() => setMostrarForm(false)}>
                        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <div className="grid-form">
                                <Field label="Compromisso">
                                    <Input name="nome_compromisso" required value={formData.nome_compromisso} onChange={handleChange} />
                                </Field>
                                <Field label="Data e hora">
                                    <Input type="datetime-local" name="data_compromisso" required
                                        value={formData.data_compromisso} onChange={handleChange} />
                                </Field>
                                <Field label="Local">
                                    <Input name="local_compromisso" placeholder="Fórum, vara, escritório..."
                                        value={formData.local_compromisso} onChange={handleChange} />
                                </Field>
                                <Field label="Status">
                                    <Select name="status_compromisso" required value={formData.status_compromisso} onChange={handleChange}>
                                        {STATUS_COMPROMISSO.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </Select>
                                </Field>
                                <Field label="Contrato (opcional)" className="span-2">
                                    <Select name="cod_contrato" value={formData.cod_contrato} onChange={handleChange}>
                                        <option value="">Compromisso avulso do escritório</option>
                                        {contratos.map((ct) => (
                                            <option key={ct.cod_contrato} value={ct.cod_contrato}>
                                                {ct.cod_contrato} — {ct.nome_cliente} ({ct.tipo_servico})
                                            </option>
                                        ))}
                                    </Select>
                                </Field>
                                <div className="field span-2">
                                    <label>Responsáveis — o compromisso aparece na agenda de cada um</label>
                                    <div className="check-lista">
                                        {equipe.map((u) => (
                                            <label key={u.id_usuario}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.ids_responsaveis.includes(u.id_usuario)}
                                                    onChange={() => toggleResponsavel(u.id_usuario)}
                                                />
                                                {u.nome} {u.OAB ? `(${u.OAB})` : ""}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <Field label="Descrição" className="span-2">
                                    <Textarea name="descricao" value={formData.descricao} onChange={handleChange} />
                                </Field>
                            </div>
                            <div className="modal-acoes">
                                {erro && <p className="erro">{erro}</p>}
                                <Button variante="secondary" onClick={() => setMostrarForm(false)}>Voltar</Button>
                                <Button type="submit" disabled={carregando}>{carregando ? "Salvando..." : "Salvar"}</Button>
                            </div>
                        </form>
                    </Modal>
                )}
            </main>
        </div>
    );
};

export default Agenda;
